import { CheckCircle2Icon, CircleDashedIcon, Loader2Icon, SparklesIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { MoodboardDepartment } from '@backend/lib/db/schema/moodboard'
import type { AnalyzeStreamState, DeptStatus } from '../use-analyze-stream'

type Props = {
  state: AnalyzeStreamState
  // Only render cards for the analyzers that are actually running this pass. Defaults to
  // all when omitted.
  enabledAnalyzers?: MoodboardDepartment[]
}

const DEPT_TITLES: Record<MoodboardDepartment, string> = {
  strategy: 'Strategy',
  voice: 'Voice',
  design: 'Design',
}

// Live view of the analyze pipeline. Three section types, top to bottom:
//   1. Extract — facts pre-processing; list of steps, struck out as they complete.
//   2. Three department cards — each carries a memo that grows token-by-token.
//   3. Summary — the brief skeleton fills in as the summarizer streams its JSON.
// Everything is always rendered so the panel doesn't reflow as phases progress.

export function StreamingAnalysisPanel({ state, enabledAnalyzers }: Props) {
  const activeDepts = (Object.keys(DEPT_TITLES) as MoodboardDepartment[]).filter(
    (dept) => !enabledAnalyzers || enabledAnalyzers.includes(dept),
  )
  const extractActive = state.phase === 'extract'
  const extractDone = !extractActive && state.phase !== 'idle' && state.phase !== 'error'
  // With on-drop enrichment, the pre-extract phase usually has nothing to do — items
  // already have their scrapes / video analyses cached. Hide the card when there are
  // no steps so we don't show empty "Reading the pile" boilerplate.
  const showExtractCard = state.extract.steps.length > 0

  const summarizeStatus: DeptStatus =
    state.phase === 'summarize'
      ? 'streaming'
      : state.phase === 'done' || state.finalBrief
        ? 'done'
        : 'pending'

  return (
    <section>
      <div className="mb-4 flex items-center gap-2">
        <SparklesIcon size={16} className="text-accent" />
        <h2 className="text-base font-semibold">Analyzing</h2>
        <span className="text-muted-foreground text-[11px]">{phaseLabel(state.phase)}</span>
      </div>

      <div className="space-y-3">
        {showExtractCard && (
          <PhaseCard
            status={extractActive ? 'streaming' : extractDone ? 'done' : 'pending'}
            title="Reading the pile"
            meta={`${state.extract.completed.length} of ${state.extract.steps.length} steps`}>
            <ul className="text-muted-foreground space-y-0.5 text-xs">
              {state.extract.steps.map((step) => {
                const done = state.extract.completed.includes(step)
                return (
                  <li
                    key={step}
                    className={`flex items-center gap-1.5 ${done ? 'text-foreground/60 line-through' : ''}`}>
                    {done ? (
                      <CheckCircle2Icon size={11} className="shrink-0 text-emerald-500" />
                    ) : (
                      <Loader2Icon
                        size={11}
                        className="text-muted-foreground shrink-0 animate-spin"
                      />
                    )}
                    <span className="truncate">{step}</span>
                  </li>
                )
              })}
            </ul>
          </PhaseCard>
        )}

        {activeDepts.map((dept) => {
          const d = state.depts[dept]
          return (
            <PhaseCard
              key={dept}
              status={d.status}
              title={`${DEPT_TITLES[dept]} memo`}
              meta={
                d.memo
                  ? `${wordCount(d.memo)} words${d.status === 'cached' ? ' · cached' : ''}`
                  : ''
              }>
              {d.memo ? (
                <MemoPreview memo={d.memo} isStreaming={d.status === 'streaming'} />
              ) : (
                <p className="text-muted-foreground text-xs italic">
                  {d.status === 'pending' ? 'Waiting…' : 'Starting…'}
                </p>
              )}
            </PhaseCard>
          )
        })}

        <PhaseCard
          status={summarizeStatus}
          title="Assembling brief"
          meta={state.partialBrief ? `${state.partialBrief.blocks?.length ?? 0} blocks` : ''}>
          {state.partialBrief && state.partialBrief.blocks?.length ? (
            <ul className="text-muted-foreground space-y-1 text-xs">
              {state.partialBrief.blocks.map((b, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <CheckCircle2Icon size={11} className="shrink-0 text-emerald-500" />
                  <span className="truncate">{blockSummary(b)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-xs italic">
              {summarizeStatus === 'pending' ? 'Waiting on memos…' : 'Drafting blocks…'}
            </p>
          )}
        </PhaseCard>

        {state.phase === 'error' && (
          <p className="text-destructive text-xs">{state.error ?? 'Analyze failed.'}</p>
        )}
      </div>
    </section>
  )
}

function PhaseCard({
  status,
  title,
  meta,
  children,
}: {
  status: DeptStatus
  title: string
  meta?: string
  children: ReactNode
}) {
  const ring =
    status === 'streaming'
      ? 'ring-accent/40 ring-2'
      : status === 'done' || status === 'cached'
        ? 'ring-emerald-500/30'
        : 'ring-border/60'
  return (
    <div className={`bg-card/60 rounded-lg p-3 ring-1 ${ring}`}>
      <div className="mb-2 flex items-center gap-2">
        <StatusIcon status={status} />
        <h3 className="text-foreground flex-1 truncate text-sm font-medium">{title}</h3>
        {meta && (
          <span className="border-border bg-background text-foreground rounded-full border px-2 py-0.5 text-[10px]">
            {meta}
          </span>
        )}
      </div>
      {children}
    </div>
  )
}

function StatusIcon({ status }: { status: DeptStatus }) {
  if (status === 'streaming') {
    return <Loader2Icon size={14} className="text-accent shrink-0 animate-spin" />
  }
  if (status === 'done' || status === 'cached') {
    return <CheckCircle2Icon size={14} className="shrink-0 text-emerald-500" />
  }
  return <CircleDashedIcon size={14} className="text-muted-foreground shrink-0" />
}

// Memo preview shows the tail of the stream so the user sees content arriving rather
// than the static start. Fades the top so it reads as a scrolling window.
function MemoPreview({ memo, isStreaming }: { memo: string; isStreaming: boolean }) {
  const tail = memo.slice(-300)
  return (
    <div className="relative h-24 overflow-hidden">
      <div className="from-card/60 absolute inset-x-0 top-0 z-10 h-4 bg-gradient-to-b to-transparent" />
      <pre className="text-muted-foreground absolute inset-x-0 bottom-0 whitespace-pre-wrap text-[11px] leading-relaxed">
        {tail}
        {isStreaming && <span className="bg-accent/60 ml-0.5 inline-block h-3 w-1 align-middle" />}
      </pre>
    </div>
  )
}

function wordCount(text: string): number {
  const trimmed = text.trim()
  if (!trimmed) return 0
  return trimmed.split(/\s+/).length
}

function phaseLabel(phase: AnalyzeStreamState['phase']): string {
  if (phase === 'extract') return 'reading inputs'
  if (phase === 'depts') return 'department analysts at work'
  if (phase === 'summarize') return 'assembling brief'
  if (phase === 'done') return 'done'
  if (phase === 'error') return 'failed'
  return ''
}

// Very lenient — partial blocks may be missing fields while streaming.
function blockSummary(b: unknown): string {
  if (!b || typeof b !== 'object') return 'block'
  const block = b as { type?: string; label?: string }
  const t = block.type ?? 'block'
  if (block.label) return `${t} — ${block.label}`
  return t
}
